import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import pinPrompt from '../utils/pinPrompt';
import { makeRequestId } from '../features/palletCheck/palletCheckData';
import {
  itemsFor, loadMoveOptions, movableParts, movedSentence, movePieces, piecesText, stationText,
} from '../features/pallets/holdingMove';

// Move pieces between an order's holding pallets (jobq-api 2026-10-29; Mike, SO-1145: "We allow the brake operators
// to configure the pallets at this stage however the formed parts fit best after braking"). Opened from a holding
// pallet's row on a station's pallet panel (TaskPalletsPanel) and from the Pallet check screen. Two ways:
//   - move pieces OFF this pallet (all, or a count per part) onto another of the order's holding pallets, or onto a
//     NEW pallet (it gets a beacon, flashing in the bin);
//   - bring everything on another holding pallet ONTO this one (Kim's case: Q8BA's 12 onto J0C4).
// Every rule is the API's (same order, same step, laser done, the plan's weight / height): a refused choice shows the
// API's sentence; a move over the limits asks "Move anyway?" first. Each move asks the operator's PIN (never the
// admin session's; a face at a station says who).

const styles = {
  section: { borderTop: '1px solid var(--jq-border-medium, #ddd)', paddingTop: 10, marginTop: 10 },
  head: { fontWeight: 600, marginBottom: 6 },
  muted: { fontSize: '0.85rem', color: 'var(--bs-secondary-color, #6c757d)' },
  row: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 },
  qty: { width: 90 },
  target: { display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 },
  why: { fontSize: '0.85rem', color: 'var(--bs-secondary-color, #6c757d)' },
  warn: { fontSize: '0.85rem', color: 'var(--bs-warning-text-emphasis, #997404)' },
};

function palletLine(p) {
  const bits = [piecesText(p.parts) || 'nothing on it', stationText(p.station)];
  if (p.beacon_id) bits.push(p.beacon_id);
  return bits.join(' · ');
}

export default function HoldingMoveDialog({ palletId, taskId = null, show, onClose, onMoved }) {
  const [state, setState] = useState({ phase: 'loading', data: null, message: '' });
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [confirm, setConfirm] = useState(null);   // {warnings, run(override)} for a move over the plan's limits
  const requestId = useRef(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setState((s) => ({ phase: s.data ? 'ready' : 'loading', data: s.data, message: '' }));
    const r = await loadMoveOptions(palletId);
    if (mine !== seq.current) return;
    if (r.ok && r.body) setState({ phase: 'ready', data: r.body, message: '' });
    else setState({ phase: 'error', data: null, message: r.message || "Couldn't read the pallets." });
  }, [palletId]);

  useEffect(() => {
    if (!show) return undefined;
    setQty({}); setError(''); setResult(''); setConfirm(null); requestId.current = null;
    load();
    return () => { seq.current += 1; };
  }, [show, load]);

  const data = state.data;
  const here = data ? data.pallet : null;

  // One move: PIN, POST, then (409 over_limit) ask before moving anyway with the same PIN and request id.
  async function run({ from, label, body, warnings }) {
    setError(''); setResult('');
    const go = async (pin, override) => {
      setBusy(true);
      if (!requestId.current) requestId.current = makeRequestId();
      const r = await movePieces(from, {
        ...body, pin, task_id: taskId, override_weight_warning: !!override, client_request_id: requestId.current,
      });
      setBusy(false);
      if (r.ok) {
        requestId.current = null;
        setConfirm(null);
        setQty({});
        setResult(movedSentence(r.body));
        if (onMoved) onMoved(r.body);
        load();
        return;
      }
      if (r.code === 'over_limit') {
        requestId.current = null;
        setConfirm({ warnings: r.warnings.length ? r.warnings : [r.message], run: () => go(pin, true) });
        return;
      }
      requestId.current = r.status === 0 ? requestId.current : null;   // a lost answer retries the same move
      setError(r.message || "Couldn't move the pieces.");
    };
    if (warnings && warnings.length) {
      setConfirm({
        warnings,
        run: async () => {
          const pin = await pinPrompt(`Your PIN, to move ${label}:`, { autofill: false, face: true });
          if (pin) go(pin, true);
        },
      });
      return;
    }
    const pin = await pinPrompt(`Your PIN, to move ${label}:`, { autofill: false, face: true });
    if (pin) go(pin, false);
  }

  function moveOff(target) {
    const picked = itemsFor(here, qty);
    if (picked.error) { setError(picked.error); return; }
    const where = target ? target.short_id : 'a new pallet';
    run({
      from: here.pallet_id,
      label: `${picked.count} pieces from ${here.short_id} onto ${where}`,
      body: target ? { to_pallet_id: target.pallet_id, items: picked.items } : { new_pallet: true, items: picked.items },
      // the precomputed limits are for moving everything: a partial move is checked by the API (409 asks then)
      warnings: picked.items ? [] : ((target ? target.onto : data.new_pallet) || {}).warnings,
    });
  }

  function bringHere(other) {
    run({
      from: other.pallet_id,
      label: `${piecesText(movableParts(other))} from ${other.short_id} onto ${here.short_id}`,
      body: { to_pallet_id: here.pallet_id },
      warnings: (other.from || {}).warnings,
    });
  }

  const others = (data && data.others) || [];
  const parts = here ? movableParts(here) : [];
  return (
    <Modal show={show} onHide={busy ? undefined : onClose} centered size="lg" data-testid="holding-move-dialog">
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Move pieces{here ? ` · ${here.short_id}` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {state.phase === 'loading' && <div style={styles.muted} role="status">Reading the pallets…</div>}
        {state.phase === 'error' && <div className="text-danger" role="alert">{state.message}</div>}
        {result && <div className="alert alert-success py-2" role="status" data-testid="holding-move-result">{result}</div>}
        {data && here && (
          <>
            <div data-testid="holding-move-here">
              <strong>{here.short_id}</strong>{here.order_number ? ` (${here.order_number})` : ''}: {palletLine(here)}
            </div>
            {!data.movable && <div className="mt-2" style={styles.why}>{data.why}</div>}
            {data.movable && (
              <>
                <div style={styles.section}>
                  <div style={styles.head}>Move pieces off {here.short_id}</div>
                  {parts.length === 0 && <div style={styles.muted}>Nothing on it can move.</div>}
                  {parts.map((p) => (
                    <div key={p.part_id} style={styles.row}>
                      <label htmlFor={`hm-qty-${p.part_id}`}>{p.product_id}</label>
                      <input id={`hm-qty-${p.part_id}`} type="number" min="0" max={p.qty} className="form-control form-control-sm"
                        style={styles.qty} value={qty[p.part_id] === undefined ? String(p.qty) : qty[p.part_id]}
                        onChange={(e) => setQty((q) => ({ ...q, [p.part_id]: e.target.value }))} disabled={busy} />
                      <span style={styles.muted}>of {p.qty}</span>
                    </div>
                  ))}
                  {parts.length > 0 && (
                    <>
                      {others.map((o) => (
                        <div key={o.pallet_id} style={styles.target}>
                          <div>
                            <Button size="sm" variant="outline-primary" disabled={busy || !o.onto.ok}
                              onClick={() => moveOff(o)} data-testid={`holding-move-onto-${o.pallet_id}`}>
                              Onto {o.short_id}
                            </Button>{' '}
                            <span style={styles.muted}>{palletLine(o)}</span>
                          </div>
                          {!o.onto.ok && <div style={styles.why}>{o.onto.why}</div>}
                          {o.onto.ok && (o.onto.warnings || []).map((w) => <div key={w} style={styles.warn}>⚠ {w}</div>)}
                        </div>
                      ))}
                      {data.new_pallet && (
                        <div style={styles.target}>
                          <div>
                            <Button size="sm" variant="outline-primary" disabled={busy || !data.new_pallet.ok}
                              onClick={() => moveOff(null)} data-testid="holding-move-new">
                              Onto a new pallet
                            </Button>{' '}
                            <span style={styles.muted}>it gets a beacon (flashing in the bin)</span>
                          </div>
                          {!data.new_pallet.ok && <div style={styles.why}>{data.new_pallet.why}</div>}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {others.length > 0 && (
                  <div style={styles.section}>
                    <div style={styles.head}>Bring everything from another pallet onto {here.short_id}</div>
                    {others.map((o) => (
                      <div key={o.pallet_id} style={styles.target}>
                        <div>
                          <Button size="sm" variant="outline-primary" disabled={busy || !o.from.ok}
                            onClick={() => bringHere(o)} data-testid={`holding-move-from-${o.pallet_id}`}>
                            {o.from.ok ? `Bring ${piecesText(movableParts(o))} from ${o.short_id}` : `From ${o.short_id}`}
                          </Button>{' '}
                          <span style={styles.muted}>{palletLine(o)}</span>
                        </div>
                        {!o.from.ok && <div style={styles.why}>{o.from.why}</div>}
                        {o.from.ok && (o.from.warnings || []).map((w) => <div key={w} style={styles.warn}>⚠ {w}</div>)}
                      </div>
                    ))}
                  </div>
                )}
                {others.length === 0 && (
                  <div className="mt-2" style={styles.muted}>This order has no other holding pallet to combine with.</div>
                )}
              </>
            )}
          </>
        )}
        {confirm && (
          <div className="alert alert-warning mt-3 mb-0" role="alertdialog" data-testid="holding-move-confirm">
            {confirm.warnings.map((w) => <div key={w}>{w}</div>)}
            <div className="mt-2 d-flex gap-2">
              <Button size="sm" variant="warning" disabled={busy} onClick={() => confirm.run()}>Move anyway</Button>
              <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => setConfirm(null)}>Don't move</Button>
            </div>
          </div>
        )}
        {error && <div className="text-danger mt-2" role="alert" data-testid="holding-move-error">{error}</div>}
        {busy && <div className="mt-2" style={styles.muted} role="status">Moving…</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Done</Button>
      </Modal.Footer>
    </Modal>
  );
}
